/**
 * HTML-escaping primitives for the message export / print-to-PDF path.
 *
 * SEC-I1 (stored XSS through an exported clinical response). `Message.handleDownload`
 * assembles a complete HTML document by string concatenation and hands it to
 * `printWindow.document.write()`. That window comes from `window.open('', '_blank')`,
 * i.e. an `about:blank` document that INHERITS the app's origin — anything that
 * executes there runs inside the clinician's authenticated session, with read access
 * to the access token in session/localStorage and the ability to call the March API
 * as that clinician.
 *
 * Every value interpolated into that document is untrusted: the narrative, the
 * structured sections, the widget payloads and the follow-up suggestions are all
 * model-generated, and the model's input includes retrieved documents that can carry
 * attacker-authored text (the same prompt-injection surface the backend hardening
 * covers). Before this module those interpolations were raw, so an `<img src=x
 * onerror=...>` sitting in any narrative, table cell, widget field or suggestion
 * string became live markup in a same-origin window.
 *
 * The fix is structural rather than per-call-site: `safeHtml` is a tagged template
 * that escapes EVERY interpolated value by default, so a newly added `${...}` is safe
 * unless its author deliberately opts out via `rawHtml()`. Escaping by hand at each
 * site would regress the first time someone adds an interpolation and forgets the
 * wrapper — which is exactly how the one hand-rolled `esc()` helper that already
 * existed in that function ended up covering 2 of ~120 interpolations.
 */

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

/** Escape a value for interpolation into HTML text or a quoted attribute. */
export function escapeHtml(value: unknown): string {
  return String(value ?? '').replace(/[&<>"']/g, (ch) => HTML_ESCAPES[ch]);
}

/**
 * Marker class for a fragment that is already escaped (or is literal markup we
 * generated ourselves). `safeHtml` passes these through untouched — everything else
 * is escaped.
 */
class RawHtml {
  constructor(readonly value: string) {}
  toString(): string {
    return this.value;
  }
}

/** Mark a string as already-safe HTML so `safeHtml` will not re-escape it. */
export function rawHtml(value: string): RawHtml {
  return new RawHtml(value);
}

/**
 * Escaped text with newlines turned into `<br>`. The print layout relies on those
 * breaks, so the substitution happens AFTER escaping — never before, or the escape
 * pass would neutralise the `<br>` we just inserted.
 */
export function escapeHtmlWithBreaks(value: unknown): RawHtml {
  return rawHtml(escapeHtml(value).replace(/\r?\n/g, '<br>'));
}

/**
 * Tagged template that escapes every interpolated value.
 * Use `rawHtml(...)` for a fragment that is intentionally markup.
 */
export function safeHtml(strings: TemplateStringsArray, ...values: unknown[]): string {
  let out = strings[0];
  for (let i = 0; i < values.length; i += 1) {
    const value = values[i];
    out += (value instanceof RawHtml ? value.value : escapeHtml(value)) + strings[i + 1];
  }
  return out;
}
